'use client';

import * as React from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import type { DateRange as DayPickerDateRange } from 'react-day-picker';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useInteractions,
} from '@floating-ui/react';
import { cn } from '../lib/utils';
import { Button } from './button';
import { Calendar } from './calendar';

export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

export interface DateRangePickerProps {
  /** The selected date range */
  dateRange: DateRange | undefined;
  /** Callback when date range changes */
  onDateRangeChange: (range: DateRange | undefined) => void;
  /** Placeholder for start date */
  startPlaceholder?: string;
  /** Placeholder for end date */
  endPlaceholder?: string;
  /** Number of months to display (default: 2) */
  numberOfMonths?: number;
  /** Function to determine if a date should be disabled */
  disabled?: (date: Date) => boolean;
  /** Optional className */
  className?: string;
  /** Date format string (default: 'LLL dd, y' e.g., "Jan 01, 2024") */
  dateFormat?: string;
}

function DateRangePicker({
  dateRange,
  onDateRangeChange,
  startPlaceholder = 'Start date',
  endPlaceholder = 'End date',
  numberOfMonths = 2,
  disabled,
  className,
  dateFormat = 'LLL dd, y',
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
    strategy: 'absolute',
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const handleSelect = (range: DayPickerDateRange | undefined) => {
    onDateRangeChange(
      range
        ? {
            from: range.from,
            to: range.to,
          }
        : undefined,
    );
  };

  const formatDateRange = () => {
    if (!dateRange?.from) {
      return (
        <span className="text-muted-foreground">
          {startPlaceholder} - {endPlaceholder}
        </span>
      );
    }

    if (dateRange.to) {
      return (
        <>
          {format(dateRange.from, dateFormat)} -{' '}
          {format(dateRange.to, dateFormat)}
        </>
      );
    }

    return format(dateRange.from, dateFormat);
  };

  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-block',
      }}
    >
      <Button
        ref={refs.setReference}
        variant="outline"
        className={cn(
          'w-[300px] justify-start text-left font-normal',
          !dateRange?.from && 'text-muted-foreground',
          className,
        )}
        {...getReferenceProps()}
      >
        <CalendarIcon className="mr-2 h-4 w-4" />
        {formatDateRange()}
      </Button>
      {isOpen && (
        <div
          ref={refs.setFloating}
          style={{
            position: floatingStyles.position,
            top: floatingStyles.top,
            left: floatingStyles.left,
            transform: floatingStyles.transform,
            zIndex: 9999,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '0.375rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            padding: 0,
          }}
          {...getFloatingProps()}
        >
          <Calendar
            mode="range"
            defaultMonth={dateRange?.from}
            selected={
              dateRange
                ? { from: dateRange.from, to: dateRange.to }
                : undefined
            }
            onSelect={handleSelect}
            numberOfMonths={numberOfMonths}
            disabled={disabled}
            initialFocus
          />
        </div>
      )}
    </div>
  );
}
DateRangePicker.displayName = 'DateRangePicker';

export { DateRangePicker };
